// Radar — a player's per-template radar wheel, ported from js/renderers-radar.js's
// window.renderRadarModule via the 04b bridge. Single-player Radar VIEW:
//   - 04h-4b: the D3 wheel as an imperative island (radar-chart.ts) over the 04h-4a pure layer,
//     with season + template selects in main.js's existing sub-controls strip.
//   - 04h-4c-1: the companion Metric/Min/Value/Max table (chart axes + a Minutes row) + the
//     table-row → chart-wedge hover highlight (renderers-radar.js renderRadarTable/wireRadarTableHover).
//   - 04h-4c-2: the custom-axis editor (RadarAxisEditor) — a React-portal popover to add/remove/
//     drag-reorder mod._radarCustomAxes, which reshapes both the chart and the table.
//   - 04h-5b: the Radar⇄Distribution toggle (mod._radarView) + the single-player Distribution view
//     (RadarDistribution — density rows into the table slot, mutually exclusive with the table).
//   - 04h-5c: RadarModule is now a hookless DISPATCHER — comparison (RadarComparison, 2–5 merged
//     players overlaid on one wheel) vs single-player, on mod._mergedPlayers (the StatsModule pattern).
//   - 04h-5d: the Radar⇄Distribution toggle in comparison mode too (RadarDistributionOverlay — one
//     marker per player on the shared population curve). ⇒ Radar COMPLETE.
//
// needsPlayer:true — main.js's createModule already built the .module-sub-controls strip with
// the shared player search/select (js/main.js:2314), and its 'change' re-renders us with the
// new playerName prop. We DON'T own the header player picker (the sticky-player mirror gap
// stays open for Phase 05, same as every needsPlayer module); we ADD the season + template
// selects into that existing strip via <SubControlsSlot>.

import { whyNoShape } from "../live/radar.js";
import { startMenuDrag } from "../canvas/menu-drag.js";
import { rankingCategoryOf } from "../hooks/useRankings.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainRadar } from "./explain.js";
import { useEffect, useMemo, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { noDataMessage } from "./no-data.js";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { RadarAxisEditor, RadarEditAxesButton } from "./RadarAxisEditor.js";
import { RadarDistribution } from "./RadarDistribution.js";
import { RadarComparison, RadarComparisonLive } from "./RadarComparison.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import {
  metricFraction,
  isLowerBetter,
  withMinutesRow,
  buildMetricCatalog,
  humanizePositionTemplate,
  playerSpread,
  TEMPLATE_ORDER,
  type RadarAxisWithRange,
} from "./radar-view.js";
import {
  CHART_SKELETON_HTML,
  ensureChartFrame,
  updateRadarData,
  teardownRadarChart,
  setAxisTicksActive,
  type RadarEntry,
} from "./radar-chart.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";
import { useRadarSeason, useRadarValues } from "../hooks/useRadarView.js";
import { useRadarAxes, useRadarCategories } from "../hooks/useRadarAxes.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { playerIdOfName, type PlayerId } from "../data/players.js";
import { LIVE } from "../live/flag.js";
import { pickedShortName } from "../live/picked-players.js";
import { teamOfModule } from "../live/team.js";
import { TeamRadar } from "./TeamRadar.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";

// Hookless dispatcher: comparison when the module carries a merged-player set, else single-player.
// No hooks here, so each mode owns its own hook order across a merge/unmerge refresh — exactly the
// StatsModule pattern. The drag-to-merge GESTURE flips mod._mergedPlayers then refreshModuleBody's.
// `?live=1`: the comparison reads real numbers (T2-16b), picked at load as the one-player data is.
const Comparison = LIVE ? RadarComparisonLive : RadarComparison;
export function RadarModule(props: ProtoModuleProps) {
  // A team shown here (teams and matches, Ben 2026-10-30: one Radar, player or team).
  const team = LIVE ? teamOfModule(props.mod) : null;
  if (team) return <TeamRadar mod={props.mod} body={props.body} team={team} />;
  const merged = getModState<PlayerId[]>(props.mod, "_mergedPlayers");
  return merged && merged.length ? <Comparison {...props} /> : <RadarSinglePlayer {...props} />;
}

function RadarSinglePlayer({ mod, body, playerId, playerName }: ProtoModuleProps) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  // The custom-axis editor popover (04h-4c-2): open state + a ref to its anchor button, which
  // lives in main.js's sub-controls strip (via SubControlsSlot).
  const [editorOpen, setEditorOpen] = useState(false);
  const editBtnRef = useRef<HTMLButtonElement>(null);

  // Resolve the shown player: the module's own .player-select value, falling back to the first
  // snapshot player when absent (renderers-radar.js:1402). Live: any player in the DB by id
  // (`playerName` is null for one the snapshot lacks); the same fall-back with no player.
  const players = snapshotPlayersOrNone();
  const name = playerName && players[playerName] ? playerName : LIVE && playerId !== null ? "" : (Object.keys(players)[0] ?? "");
  // Who that is, by id — what this module stores (ids everywhere).
  const shownId = name ? playerIdOfName(players, name) : LIVE ? playerId : null;

  // Season + template are tagged with the player they belong to (_selectedSeasonPlayer) so both
  // auto-reset to the new player's currentSeason / detected position when the player changes — a
  // chosen season/template is meaningless for a different player (renderers-radar.js:1424).
  const seasonOwner = getModState<PlayerId>(mod, "_selectedSeasonPlayer");
  const requestedSeason = seasonOwner === shownId ? getModState<string>(mod, "_selectedSeason") : undefined;
  const radar = useRadarSeason(name, shownId, requestedSeason);

  const storedTemplate = seasonOwner === shownId ? getModState<string>(mod, "_selectedRadarTemplate") : undefined;
  // Live: her position in the file shown. None (no minutes there, or no file) → no template until
  // one is picked, so a guess is never stored as her choice; the "no radar data" message shows.
  const template = LIVE
    ? storedTemplate || radar?.defaultTemplate || ""
    : (storedTemplate ?? radar?.defaultTemplate ?? "");

  // The module's custom axis list (the editor's output) overrides the template default whenever set
  // (renderers-radar.js:87). Read fresh each render so an edit (which reassigns it + forceRenders)
  // reshapes the chart + table.
  const customAxes = getModState<string[]>(mod, "_radarCustomAxes") ?? null;
  const axes = useRadarAxes(template, customAxes);
  // The table shows the chart axes PLUS a Minutes row (never a chart axis — renderers-radar.js:126).
  const categories = useRadarCategories();
  const catalog = useMemo(() => buildMetricCatalog(categories), [categories]);
  const tableAxes = useMemo(() => withMinutesRow(axes, catalog), [axes, catalog]);
  // One population scan over the table axes (the superset) — the chart uses the subset. The scan is
  // the expensive part, so ranges is memoised in the hook over [template, tableAxes]. Live: her
  // values + the scale at the template's position only (Q-009), from her league's file.
  const { values, hasData, ranges, population } = useRadarValues(radar, template, tableAxes);
  const chartAxes = useMemo<RadarAxisWithRange[]>(
    () => axes.map((a) => ({ ...a, ...(ranges.get(a.key) ?? { min: 0, max: 0 }) })),
    [axes, ranges],
  );
  const tableAxesWithRange = useMemo<RadarAxisWithRange[]>(
    () => tableAxes.map((a) => ({ ...a, ...(ranges.get(a.key) ?? { min: 0, max: 0 }) })),
    [tableAxes, ranges],
  );

  // Which view — the wheel (Radar) or the population-density rows (Distribution). Persisted on the
  // element (undo/preset capture it). The two are mutually exclusive: Distribution hides the chart
  // SVG and takes over the table's slot (renderers-radar.js:1505).
  const isDist = getModState<string>(mod, "_radarView") === "distribution";
  const seasonCode = radar?.seasonCode ?? "";
  // Step 1's answer is a fresh object each render; the chart's memo keys on this instead.
  const hasPlayer = radar !== null;

  // Mirror the resolved season/template/owner onto the element so main.js's saveState/undo/preset
  // capture them (the React values are just a render mirror). renderers-radar.js:1424.
  useEffect(() => {
    if (shownId === null) return;
    setModState(mod, "_selectedSeasonPlayer", shownId);
    setModState(mod, "_selectedSeason", seasonCode);
    setModState(mod, "_selectedRadarTemplate", template);
  }, [mod, shownId, seasonCode, template]);

  // One chart entry (single player). fractions[i] is null for an axis with no value (never a
  // fabricated 0th-percentile point); values[i] rides the wedge tooltip. renderers-radar.js:1513.
  const chartEntry = useMemo<RadarEntry | null>(() => {
    if (!hasPlayer) return null;
    const fractions = chartAxes.map((a) => metricFraction(values[a.key], { min: a.min, max: a.max }, isLowerBetter(a.key)));
    const shown = chartAxes.map((a) => (values[a.key] ?? null));
    return { colorLetter: "a", fractions, values: shown, tooltipPrefix: humanizeSeasonCode(seasonCode) };
  }, [hasPlayer, values, chartAxes, seasonCode]);

  const svgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  // Build the SVG skeleton once for the module's life — the imperative island: React owns the
  // empty .radar-svg-wrap host, this fills it with the chart DOM. Tear the d3 chart down on
  // unmount (interrupt in-flight transitions) so nothing runs against a detached node. The host
  // is always rendered (the empty state just hides the layout), so this runs once and stays.
  useEffect(() => {
    const wrap = svgWrapRef.current;
    if (!wrap) return;
    wrap.innerHTML = CHART_SKELETON_HTML;
    const svg = wrap.querySelector<SVGSVGElement>(".radar-svg");
    return () => {
      if (svg) teardownRadarChart(svg);
      wrap.innerHTML = "";
    };
  }, []);

  // Draw/update the frame + polygon when the axes or the player's values change. NO teardown in
  // this cleanup — that would interrupt the 450ms update transition on every re-render.
  useEffect(() => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (!svg || !chartEntry) return;
    ensureChartFrame(svg, chartAxes);
    updateRadarData(svg, chartAxes, [chartEntry]);
  }, [chartAxes, chartEntry]);

  // Live: drag a stat out (a spoke's label, or the table's rank icon) → a Ranking of everyone on
  // it, in her league + season, with her found and highlighted (Ben, 2026-10-18 — as the Stats
  // tiles' "drag to rank"). A drag, not a click, so nothing appears that the user didn't mean.
  const onRankDrag = (e: ReactPointerEvent<HTMLElement>, metricKey: string) => {
    const categoryId = rankingCategoryOf(metricKey);
    if (!LIVE || categoryId === null || window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    hideTooltip();
    startMenuDrag(e.nativeEvent, "Ranking", 3, 5, {
      _rankingCategoryId: categoryId,
      _rankingMetricKey: metricKey,
      _rankingSeason: seasonCode,
      _rankingSourcePlayer: shownId,
      _rankingJumpedPlayer: shownId,
    });
  };

  const onSeasonChange = (next: string) => {
    setModState(mod, "_selectedSeason", next);
    setModState(mod, "_selectedSeasonPlayer", shownId);
    body.scrollTop = 0;
    forceRender();
  };
  const onToggleView = () => {
    setModState(mod, "_radarView", isDist ? "radar" : "distribution");
    forceRender();
  };
  const onTemplateChange = (next: string) => {
    setModState(mod, "_selectedRadarTemplate", next);
    setModState(mod, "_selectedSeasonPlayer", shownId);
    // A customization is scoped to the axis set it was built against — switching template starts
    // fresh with that template's default list (renderers-radar.js:1472). Custom axes are 04h-4c-2;
    // clearing here keeps that behaviour correct once they land.
    setModState(mod, "_radarCustomAxes", null);
    forceRender();
  };

  // Table-row → chart hover (renderers-radar.js:622 wireRadarTableHover): hovering a table row
  // highlights that axis's whole wedge on the chart + brightens its ticks + raises the same tooltip
  // the wedge shows on direct hover. Reads into the imperative-island SVG (read-only class toggles,
  // like the wedge's own hover). The Minutes row has no chart wedge → a harmless no-op.
  const wedgeFor = (axisKey: string): SVGElement | null => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (!svg) return null;
    return [...svg.querySelectorAll<SVGElement>(".radar-wedge")].find((w) => w.dataset.axisKey === axisKey) ?? null;
  };
  const onRowEnter = (axisKey: string) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    const wedge = wedgeFor(axisKey);
    if (!svg || !wedge) return;
    wedge.classList.add("row-hover");
    setAxisTicksActive(svg, axisKey, true);
    if (wedge.dataset.tooltip) {
      const rect = wedge.getBoundingClientRect();
      showTooltipAt(wedge.dataset.tooltip, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  };
  const onRowLeave = (axisKey: string) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    const wedge = wedgeFor(axisKey);
    if (svg && wedge) {
      wedge.classList.remove("row-hover");
      setAxisTicksActive(svg, axisKey, false);
    }
    hideTooltip();
  };

  // No player at all (empty snapshot) — the legacy "No data." state.
  if (!radar || (!name && !LIVE)) {
    return <div className="stats-empty">No data.</div>;
  }

  const shortName = players[name] ? getPlayerShortName(players[name]) : LIVE ? pickedShortName(shownId) : name;

  const seasonItems: StyledSelectItem[] = radar.seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) }));
  const templateItems: StyledSelectItem[] = TEMPLATE_ORDER.map((t) => ({ value: t, label: humanizePositionTemplate(t) }));

  const radarLeague = radar.src.kind === "live" && radar.src.group.kind === "file" ? radar.src.group.league.label : null;
  // Live: why there's no shape — the positions she has minutes at, or not in the data at all.
  const whyEmpty =
    LIVE && radar.src.kind === "live" && radar.src.group.kind === "file" && template && shownId !== null
      ? whyNoShape(radar.src.group.group.file, shownId, template, shortName, `${radarLeague} ${humanizeSeasonCode(seasonCode)}`)
      : noDataMessage("radar", shortName, seasonCode);
  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainRadar({
            name: shortName,
            season: humanizeSeasonCode(seasonCode),
            template: template ? humanizePositionTemplate(template) : "",
            league: radarLeague,
            distribution: isDist,
            empty: hasData ? null : radar.noFile !== null ? `There's no radar: ${radar.noFile}.` : whyEmpty,
          })}
        />
      )}
      {/* Season select, template select, then the edit-axes button — DOM order is visual order in
          the flex strip; the button sits rightmost (renderers-radar.js:1365/1386). */}
      <SubControlsSlot mod={mod}>
        <StyledSelect items={seasonItems} value={seasonCode} onChange={onSeasonChange} wrapClass="season-select-wrap" extraClass="season-select" />
        <StyledSelect items={templateItems} value={template} onChange={onTemplateChange} wrapClass="template-select-wrap" extraClass="template-select" />
        {/* The button names the view a click switches TO (Ben, 2026-10-31: on the wheel it reads
            "Distribution"). The prototype named the active view instead. */}
        <button type="button" className="module-switch-btn radar-view-toggle" onClick={onToggleView}>
          {isDist ? "Radar" : "Distribution"}
        </button>
        <RadarEditAxesButton open={editorOpen} btnRef={editBtnRef} onToggle={() => setEditorOpen((o) => !o)} />
      </SubControlsSlot>

      {editorOpen && editBtnRef.current && (
        <RadarAxisEditor
          anchor={editBtnRef.current}
          mod={mod}
          template={template}
          categories={categories}
          catalog={catalog}
          onChange={forceRender}
          onClose={() => setEditorOpen(false)}
        />
      )}

      <div className="radar-wrap">
        <div className="stats-empty" style={hasData ? { display: "none" } : undefined}>
          {hasData ? "" : radar.noFile !== null ? `No radar: ${radar.noFile}` : whyEmpty}
        </div>
        {/* radar-body-layout is a column: chart on top, table (or the Distribution rows) below. The
            wheel's SVG is kept mounted but hidden in Distribution view — toggling back is instant,
            no rebuild (renderers-radar.js:1506) — and .radar-table-wrap holds EITHER the table OR the
            Distribution rows (the two views are mutually exclusive, sharing the one slot). */}
        <div className="radar-body-layout" style={hasData ? undefined : { display: "none" }}>
          <div
            className={`radar-svg-wrap${LIVE ? " radar-labels-draggable" : ""}`}
            ref={svgWrapRef}
            style={isDist ? { display: "none" } : undefined}
            onPointerDown={(e) => {
              const key = (e.target as HTMLElement).closest<HTMLElement>(".radar-axis-label")?.dataset.axisKey;
              if (key) onRankDrag(e, key);
            }}
          />
          <div className="radar-table-wrap">
            {isDist ? (
              <RadarDistribution
                spread={playerSpread(population, template, humanizePositionTemplate(template))}
                axes={chartAxes}
                values={values}
              />
            ) : (
              <table className="radar-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Min</th>
                    <th>Value</th>
                    <th>Max</th>
                    {LIVE && <th aria-label="Rank" />}
                  </tr>
                </thead>
                <tbody>
                  {tableAxesWithRange.map((axis) => (
                    <tr
                      key={axis.key}
                      data-axis-key={axis.key}
                      onMouseEnter={() => onRowEnter(axis.key)}
                      onMouseLeave={() => onRowLeave(axis.key)}
                    >
                      <td className="radar-table-metric">{axis.label}</td>
                      <td>{formatMetricValue(axis.min, axis.outputType)}</td>
                      <td className="radar-table-value">{formatMetricValue(values[axis.key], axis.outputType)}</td>
                      <td>{formatMetricValue(axis.max, axis.outputType)}</td>
                      {LIVE && (
                        <td className="radar-rank-cell">
                          {rankingCategoryOf(axis.key) !== null && (
                            <span className="pbar-rank-icon" data-tooltip="Drag to rank every player" onPointerDown={(e) => onRankDrag(e, axis.key)}>
                              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3">
                                <rect x="1.5" y="2.5" width="13" height="11" rx="1.2" />
                                <line x1="1.5" y1="6.17" x2="14.5" y2="6.17" />
                                <line x1="1.5" y1="9.83" x2="14.5" y2="9.83" />
                                <line x1="6.5" y1="2.5" x2="6.5" y2="13.5" />
                              </svg>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
